import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Select,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import CustomFieldLabel from '../../components/customfield-label/CustomFieldLabel';
import PageLoader from '../../components/layout/page-loader/PageLoader';
import { ProviderName } from '../../components/provider-name/ProviderName';
import ResourceCount from '../../components/resource-count-text/ResourceCount';
import { usePermissions } from '../../hooks/usePermissions';

import { getMarketList } from '../../services/markets/getMarketList';
import { getPracticeById } from '../../services/practices/getPracticeById'; // Import the API
import { getPracticesList } from '../../services/practices/getPractices';
import { getProvidersList } from '../../services/providers/getProvidersList';
import { State } from '../../types/markets/types';
import { Practice, Provider } from '../../types/practices/types';
import {
  applyExternalFilterForNonAdmin,
  isExternalMarket,
  isExternalPractice,
} from '../../utils/externalEntity';
import { getCustomFieldValue } from '../../utils/customFieldUtils';
import { getNewPatientPriorityProps } from '../../utils/getNewPatientPriorityProps';
import ExternalAffordance from '../../components/external-indicator/ExternalAffordance';

const StateMarketList = () => {
  const navigate = useNavigate();
  const { isAdmin, permissions, isReady } = usePermissions();

  const toolbarButtonProps = {
    variant: 'outline' as const,
    borderColor: 'border.gray',
    bg: 'white',
    color: 'text.primary',
    height: '2.5rem',
    px: 4,
    borderRadius: 'md',
    fontWeight: 500,
    _hover: {
      bg: 'background.blue50',
      borderColor: 'text.brandLight',
    },
    _active: {
      bg: 'background.blue50',
    },
  };
  const toolbarSelectProps = {
    bg: 'white',
    borderWidth: '1px',
    borderColor: 'border.gray',
    color: 'text.primary',
    height: '2.5rem',
    borderRadius: 'md',
    fontWeight: 500,
    _hover: {
      bg: 'background.blue50',
      borderColor: 'text.brandLight',
    },
  };
  const selectBorderColor = 'border.gray';
  const [statesData, setStatesData] = useState<State[]>([]);
  const [expandedStates, setExpandedStates] = useState(new Set());
  const [expandedMarkets, setExpandedMarkets] = useState(
    new Set<string>()
  );
  const [expandedPractices, setExpandedPractices] = useState(
    new Set<string>()
  );
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('az');
  const [practices, setPractices] = useState<
    Record<string, Practice[]>
  >({});
  const [loadingMarkets, setLoadingMarkets] = useState<
    Record<string, boolean>
  >({});
  const [loadingPractices, setLoadingPractices] = useState<
    Record<string, boolean>
  >({});
  const [providers, setProviders] = useState<
    Record<string, Provider[]>
  >({});
  const [duplicateProviders, setDuplicateProviders] = useState<
    Record<string, Record<string, string>>
  >({});

  useEffect(() => {
    // Wait until permissions are fully initialized so we never act on the
    // default isAdmin=false before the real value is known.
    if (!isReady) return;

    const adminFlag = permissions.isAdmin;

    const filterStates = (states: State[]): State[] => {
      if (adminFlag) return states;
      return states
        .map((state) => {
          const visibleMarkets = (state.markets || []).filter(
            (market) => !isExternalMarket(market)
          );
          return {
            ...state,
            markets: visibleMarkets,
            practiceCount: visibleMarkets.reduce(
              (count, market) => count + (market.practiceCount || 0),
              0
            ),
            providerCount: visibleMarkets.reduce(
              (count, market) => count + (market.providerCount || 0),
              0
            ),
            plansCount: visibleMarkets.reduce(
              (count, market) => count + (market.plansCount || 0),
              0
            ),
          };
        })
        .filter((state) => state.markets.length > 0);
    };

    const fetchMarkets = async (retryCount = 0) => {
      try {
        // Show cached data immediately, filtered for the current session's role.
        // Raw states are stored in cache (not pre-filtered) so an admin-session
        // cache is still correctly filtered when a non-admin loads the page.
        const cachedStates = localStorage.getItem('statesData');
        if (cachedStates) {
          setStatesData(filterStates(JSON.parse(cachedStates)));
          setLoading(false);
        }

        const { data } = await getMarketList(
          undefined,
          undefined,
          undefined,
          adminFlag
        );
        if (data && data.states) {
          localStorage.setItem(
            'statesData',
            JSON.stringify(data.states)
          );
          setStatesData(filterStates(data.states));
        } else {
          throw new Error('No market data available.');
        }
      } catch (error) {
        console.error('Failed to fetch market data:', error);
        if (retryCount < 3) {
          setTimeout(() => fetchMarkets(retryCount + 1), 3000);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMarkets();
  }, [isReady, permissions.isAdmin]);

  const toggleState = (stateName: string) => {
    const newExpandedStates = new Set(expandedStates);
    if (newExpandedStates.has(stateName)) {
      newExpandedStates.delete(stateName);
    } else {
      newExpandedStates.add(stateName);
    }
    setExpandedStates(newExpandedStates);
  };
  const toggleMarket = (marketId: string) => {
    setExpandedMarkets((prev) => {
      const newExpandedMarkets = new Set(prev);
      if (newExpandedMarkets.has(marketId)) {
        newExpandedMarkets.delete(marketId);
      } else {
        newExpandedMarkets.add(marketId);
        fetchPractices(marketId);
      }
      return newExpandedMarkets;
    });
  };
  const togglePractice = (practiceId: string) => {
    setExpandedPractices((prev) => {
      const newExpandedPractices = new Set(prev);
      if (newExpandedPractices.has(practiceId)) {
        newExpandedPractices.delete(practiceId);
      } else {
        newExpandedPractices.add(practiceId);
        fetchPracticeDetails(practiceId);
      }
      return newExpandedPractices;
    });
  };

  const fetchPractices = async (marketId: string) => {
    try {
      setLoadingMarkets((prev) => ({ ...prev, [marketId]: true }));
      if (!practices[marketId]) {
        const { data } = await getPracticesList(
          null,
          applyExternalFilterForNonAdmin({
            marketIds: marketId,
          }, isAdmin()),
          10
        );
        setPractices((prev) => ({
          ...prev,
          [marketId]: data?.practices || [],
        }));
      }
    } catch (error) {
      console.error('Failed to fetch practices:', error);
    } finally {
      setLoadingMarkets((prev) => ({ ...prev, [marketId]: false }));
    }
  };

  const fetchPracticeDetails = async (practiceId: string) => {
    try {
      setLoadingPractices((prev) => ({
        ...prev,
        [practiceId]: true,
      }));
      if (!providers[practiceId]) {
        const { data } = await getPracticeById(practiceId);
        setProviders((prev) => ({
          ...prev,
          [practiceId]: data?.providers || [],
        }));

        // Fetch duplicate providers for this practice
        try {
          const { data: providersData } = await getProvidersList(
            null,
            applyExternalFilterForNonAdmin(
              { practiceId },
              isAdmin()
            ),
            1000
          );
          setDuplicateProviders((prev) => ({
            ...prev,
            [practiceId]: providersData?.duplicateProviders || {},
          }));
        } catch (duplicateError) {
          console.warn(
            'Failed to fetch duplicate providers:',
            duplicateError
          );
          setDuplicateProviders((prev) => ({
            ...prev,
            [practiceId]: {},
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch practice details:', error);
    } finally {
      setLoadingPractices((prev) => ({
        ...prev,
        [practiceId]: false,
      }));
    }
  };

  const toggleAll = (expand: boolean) => {
    if (expand) {
      setExpandedStates(
        new Set(statesData.map((state) => state.name))
      );
    } else {
      setExpandedStates(new Set());
    }
  };

  const sortStates = (order: string) => {
    setSortOrder(order);
  };

  const navigateToStatePage = (stateName: string) => {
    navigate(`/states/${stateName}`);
  };

  const navigateToMarketPage = (marketId: string) => {
    navigate(`/markets/${marketId}`);
  };

  const navigateToPracticePage = (practiceId: string) => {
    navigate(`/practices/${practiceId}`);
  };

  const navigateToProviderPage = (providerId: string) => {
    navigate(`/providers/${providerId}`);
  };

  if (loading) {
    return <PageLoader height="100vh" />;
  }

  return (
    <Box w="100%" maxW="100%" mb="7.69rem">
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb="1.5rem"
        gap={4}
        direction={{ base: 'column', md: 'row' }}
        flexWrap="wrap"
      >
        <Text
          fontSize="lg"
          fontWeight={600}
          lineHeight="short"
          color="text.primary"
        >
          Browse markets by state
        </Text>
        <HStack gap={3} flexWrap="wrap" justify={{ base: 'flex-start', md: 'flex-end' }}>
          {isAdmin() && (
            <Button
              {...toolbarButtonProps}
              onClick={() => navigate('/markets/create')}
            >
              Add Market
            </Button>
          )}
          <Button
            {...toolbarButtonProps}
            onClick={() =>
              expandedStates.size === statesData.length
                ? toggleAll(false)
                : toggleAll(true)
            }
          >
            {expandedStates.size === statesData.length
              ? 'Collapse all'
              : 'Expand all'}
          </Button>

          <Select
            {...toolbarSelectProps}
            w={{ base: '100%', sm: '10.375rem' }}
            minW="10rem"
            cursor="pointer"
            value={sortOrder}
            onChange={(e) => sortStates(e.target.value)}
          >
            <option value="az">A - Z</option>
            <option value="za">Z - A</option>
          </Select>
        </HStack>
      </Flex>

      <VStack spacing={4} align="stretch">
        {[...statesData]
          .sort((a, b) => {
            return sortOrder === 'az'
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          })
          .map((state) => (
            <Box
              key={state.name}
              border="1px"
              borderColor={selectBorderColor}
              borderRadius="md"
              p="1rem"
            >
              <HStack justify="space-between" align="flex-start" spacing={4}>
                <Flex flexWrap="wrap" align="baseline" gap={{ base: 2, md: 3 }} rowGap={2}>
                  <Text
                    fontSize={{ base: 'lg', md: 'xl' }}
                    fontWeight={700}
                    lineHeight="short"
                    color="background.brandDark"
                    _hover={{
                      color: 'text.brandLight',
                      transform: 'scale(0.98)',
                    }}
                    cursor="pointer"
                    mr={{ base: 0, md: '1rem' }}
                    onClick={() => navigateToStatePage(state.name)} // Navigate to state page
                  >
                    {state.name}
                  </Text>
                  <HStack gap={2} flexWrap="wrap">
                    <ResourceCount
                      count={state.markets.length}
                      resource="Markets"
                    />
                    <ResourceCount
                      count={state.practiceCount}
                      resource="Practices"
                    />
                    <ResourceCount
                      count={state.providerCount}
                      resource="Providers"
                    />
                    <ResourceCount
                      count={state.plansCount}
                      resource="Plans"
                    />
                  </HStack>
                </Flex>
                <Button
                  size="sm"
                  onClick={() => toggleState(state.name)}
                  variant="link"
                  colorScheme="blue"
                >
                  <Flex
                    gap="0.5rem"
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Text>
                      {expandedStates.has(state.name)
                        ? 'Collapse'
                        : 'Expand'}
                    </Text>
                    {expandedStates.has(state.name) ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </Flex>
                </Button>
              </HStack>
              <Collapse
                in={expandedStates.has(state.name)}
                animateOpacity
              >
                <VStack
                  align="stretch"
                  mt="1.31rem"
                  spacing={'0.5rem'}
                  gap="0.5rem"
                  width="100%"
                >
                  {[...state.markets]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((market) => (
                      <Box
                        key={market.id}
                        border="1px"
                        borderColor={selectBorderColor}
                        borderRadius="md"
                        p="1rem"
                        width="100%"
                      >
                        <Flex
                          justify="space-between"
                          alignItems="center"
                          width="100%"
                        >
                          <Flex alignItems="center" gap="0.5rem">
                            <Text
                              color="background.brandLight"
                              fontSize="1rem"
                              fontWeight={500}
                              lineHeight={'120%'}
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              cursor={'pointer'}
                              onClick={() =>
                                navigateToMarketPage(market.id)
                              }
                            >
                              {market.name}
                            </Text>
                            {(isAdmin() && market.external) ? (
                              <Flex
                                justify="center"
                                alignItems="center"
                                px="0.625rem"
                                py="0.25rem"
                                borderRadius="0.375rem"
                                background="orange.100"
                                border="1px solid"
                                borderColor="orange.300"
                                cursor="default"
                              >
                                <Text
                                  color="orange.800"
                                  fontSize="0.75rem"
                                  fontWeight={600}
                                  lineHeight="1rem"
                                >
                                  External
                                </Text>
                              </Flex>
                            ) : market.tablespace ? (
                              <Tooltip
                                label={`Tablespace: ${market.tablespace}`}
                                hasArrow
                              >
                                <Flex
                                  justify="center"
                                  alignItems="center"
                                  px="0.625rem"
                                  py="0.25rem"
                                  borderRadius="0.375rem"
                                  background="background.green100"
                                  border="1px solid"
                                  borderColor="green.300"
                                  cursor="default"
                                >
                                  <Text
                                    color="green.800"
                                    fontSize="0.75rem"
                                    fontWeight={600}
                                    lineHeight="1rem"
                                  >
                                    {market.tablespace}
                                  </Text>
                                </Flex>
                              </Tooltip>
                            ) : null}
                          </Flex>
                          <Flex>
                            <HStack gap="0.38rem">
                              <Flex>
                                <ResourceCount
                                  count={market.practiceCount}
                                  resource={'Practices'}
                                />
                              </Flex>
                            </HStack>
                            <Button
                              size="sm"
                              onClick={() => toggleMarket(market.id)}
                              variant="link"
                              colorScheme="blue"
                            >
                              <Flex
                                gap="0.5rem"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {expandedMarkets.has(market.id) ? (
                                  <ChevronUpIcon />
                                ) : (
                                  <ChevronDownIcon />
                                )}
                              </Flex>
                            </Button>
                          </Flex>
                        </Flex>
                        <Collapse
                          in={expandedMarkets.has(market.id)}
                          animateOpacity
                        >
                          {loadingMarkets[market.id] ? (
                            <Flex
                              justify="center"
                              align="center"
                              mt="1rem"
                            >
                              <Spinner
                                size="sm"
                                color="text.brandLight"
                              />
                            </Flex>
                          ) : (
                            <VStack
                              align="stretch"
                              mt="1rem"
                              spacing={'0.5rem'}
                            >
                              {practices[market.id]?.map(
                                (practice) => (
                                  <Box
                                    key={practice.id}
                                    border="1px"
                                    borderColor={selectBorderColor}
                                    borderRadius="md"
                                    p="1rem"
                                  >
                                    <HStack justify={'space-between'}>
                                      <HStack gap="0.5rem" align="center">
                                        <Text
                                          color="text.brandLight"
                                          fontSize="0.875rem"
                                          fontStyle="normal"
                                          fontWeight="500"
                                          lineHeight="1rem"
                                          cursor={'pointer'}
                                          onClick={() =>
                                            navigateToPracticePage(
                                              practice.id
                                            )
                                          } // Navigate to practice page
                                        >
                                          {practice.name}
                                        </Text>
                                        {isExternalPractice(practice) && (
                                          <ExternalAffordance
                                            entityType="practice"
                                            size="sm"
                                          />
                                        )}
                                      </HStack>

                                      <Button
                                        size="sm"
                                        onClick={() =>
                                          togglePractice(practice.id)
                                        }
                                        variant="link"
                                        colorScheme="blue"
                                      >
                                        {expandedPractices.has(
                                          practice.id
                                        ) ? (
                                          <ChevronUpIcon />
                                        ) : (
                                          <ChevronDownIcon />
                                        )}
                                      </Button>
                                    </HStack>
                                    <Collapse
                                      in={expandedPractices.has(
                                        practice.id
                                      )}
                                      animateOpacity
                                    >
                                      {loadingPractices[
                                        practice.id
                                      ] ? (
                                        <Flex
                                          justify="center"
                                          align="center"
                                          mt="1rem"
                                        >
                                          <Spinner size="sm" />
                                        </Flex>
                                      ) : (
                                        <VStack
                                          align="stretch"
                                          mt="1rem"
                                          spacing={'0.5rem'}
                                        >
                                          {providers[
                                            practice.id
                                          ]?.map(
                                            (provider, index) => {
                                              const newPatientProps =
                                                getNewPatientPriorityProps(
                                                  getCustomFieldValue(
                                                    provider.customFields,
                                                    'New-Patient'
                                                  )
                                                );

                                              return (
                                                <Flex
                                                  key={index}
                                                  alignItems="flex-start"
                                                  //justifyContent="space-between"
                                                  flexWrap="wrap"
                                                  gap="1rem"
                                                  p="0.5rem"
                                                >
                                                  <ProviderName
                                                    provider={
                                                      provider
                                                    }
                                                    duplicateProviders={
                                                      duplicateProviders[
                                                        practice.id
                                                      ] || {}
                                                    }
                                                    mode="link"
                                                    size="sm"
                                                    showTooltip={true}
                                                    highlightDuplicates={
                                                      true
                                                    }
                                                    duplicatesBehavior="popover"
                                                    onNavigate={(
                                                      id
                                                    ) =>
                                                      navigateToProviderPage(
                                                        id
                                                      )
                                                    }
                                                  />

                                                  <Flex gap="0.75rem">
                                                    {newPatientProps &&
                                                      newPatientProps.fieldValue && (
                                                        <CustomFieldLabel
                                                          customField="New Patient Priority"
                                                          fieldValue={
                                                            newPatientProps.fieldValue
                                                          }
                                                          fieldValueBgColor={
                                                            newPatientProps.fieldValueBgColor
                                                          }
                                                          fieldValueTextColor={
                                                            newPatientProps.fieldValueTextColor
                                                          }
                                                          hideCustomFieldLabel={
                                                            false
                                                          }
                                                        />
                                                      )}
                                                    {getCustomFieldValue(
                                                      provider.customFields,
                                                      'Age'
                                                    ) && (
                                                      <CustomFieldLabel
                                                        customField="Treats"
                                                        fieldValue={getCustomFieldValue(
                                                          provider.customFields,
                                                          'Age'
                                                        )}
                                                        fieldValueBgColor="background.teal100"
                                                        fieldValueTextColor="text.primary"
                                                      />
                                                    )}
                                                    {provider.specialty && (
                                                      <CustomFieldLabel
                                                        customField="Speciality"
                                                        fieldValue={
                                                          provider.specialty
                                                        }
                                                        fieldValueBgColor="background.blue100"
                                                        fieldValueTextColor="text.primary"
                                                      />
                                                    )}
                                                  </Flex>
                                                </Flex>
                                              );
                                            }
                                          )}
                                        </VStack>
                                      )}
                                    </Collapse>
                                  </Box>
                                )
                              )}
                            </VStack>
                          )}
                        </Collapse>
                      </Box>
                    ))}
                </VStack>
              </Collapse>
            </Box>
          ))}
      </VStack>
    </Box>
  );
};

export default StateMarketList;
